/**
 * 📨 SMTP Servers Component
 * Vollständige SMTP-Server-Verwaltung basierend auf mail_gateway.php
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Server, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  TestTube,
  Zap,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../services/AuthService';
import BulkSmtpCreator from './BulkSmtpCreator';
import CsvSmtpImport from './CsvSmtpImport';

const SmtpServers = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [showBulkParser, setShowBulkParser] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [proxyResetLoading, setProxyResetLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  const performBulkDelete = async (mode) => {
    if (mode !== 'all' && mode !== 'inactive') return;
    const toDelete = mode === 'all'
      ? (servers || [])
      : (servers || []).filter((s) => s.is_active === 0);

    if (toDelete.length === 0) {
      toast.error(mode === 'all' ? 'Keine SMTPs vorhanden' : 'Keine inaktiven SMTPs vorhanden');
      return;
    }

    setBulkDeleteLoading(true);
    const toastId = toast.loading('Lösche SMTPs...');
    try {
      const ids = toDelete.map((s) => s.id);
      const BATCH = 500;
      let totalDeleted = 0;

      for (let i = 0; i < ids.length; i += BATCH) {
        const batch = ids.slice(i, i + BATCH);
        toast.loading(`Lösche... ${Math.min(i + BATCH, ids.length)}/${ids.length}`, { id: toastId });
        try {
          const resp = await axios.post('/api/smtp/bulk-delete', { ids: batch });
          totalDeleted += resp.data.deleted || 0;
        } catch (err) {
          console.error('Bulk delete batch failed:', err);
        }
      }

      toast.success(`✅ ${totalDeleted} SMTPs gelöscht!`, { id: toastId });
      queryClient.invalidateQueries('smtpServers');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Fehler beim Löschen', { id: toastId });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Admin: Proxy Reset Handler
  const handleProxyReset = async () => {
    if (!window.confirm('Alle 19 LTE-Proxies jetzt resetten? Dies dauert ca. 20 Sekunden.')) {
      return;
    }
    
    setProxyResetLoading(true);
    const toastId = toast.loading('🔄 Resette alle LTE-Proxies...');
    
    try {
      const response = await axios.post('/api/admin/proxies/reset');
      const data = response.data;
      
      if (data.success) {
        toast.success(`✅ ${data.message}`, { id: toastId, duration: 5000 });
      } else {
        toast.error(`❌ ${data.error || 'Reset fehlgeschlagen'}`, { id: toastId });
      }
    } catch (e) {
      toast.error(`❌ Fehler: ${e.response?.data?.error || e.message}`, { id: toastId });
    } finally {
      setProxyResetLoading(false);
    }
  };

  // Load SMTP servers
  const { data: servers, isLoading } = useQuery('smtpServers', async () => {
    const response = await axios.get('/api/smtp');
    return response.data.data;
  });

  // Add server mutation
  const addServerMutation = useMutation(
    (serverData) => axios.post('/api/smtp', serverData),
    {
      onSuccess: () => {
        toast.success('SMTP-Server erfolgreich hinzugefügt!');
        setShowAddForm(false);
        queryClient.invalidateQueries('smtpServers');
      },
      onError: (error) => {
        toast.error('Fehler beim Hinzufügen des SMTP-Servers');
        console.error('SMTP add error:', error);
      }
    }
  );

  // Test server mutation
  const testServerMutation = useMutation(
    ({ id, testType, testEmail }) => axios.post(`/api/smtp/${id}/test`, { testType, testEmail }),
    {
      onSuccess: (response, variables) => {
        const data = response.data.data;
        let message = `SMTP Test:\n`;
        if (data.tests.connection) message += `Connection: ${data.tests.connection.success ? '✅' : '❌'} ${data.tests.connection.message || data.tests.connection.error}\n`;
        if (data.tests.proxy) message += `Proxy: ${data.tests.proxy.success ? '✅' : '❌'} ${data.tests.proxy.message || data.tests.proxy.error}\n`;
        if (data.tests.testmail) message += `Test-Mail: ${data.tests.testmail.success ? '✅' : '❌'} ${data.tests.testmail.message || data.tests.testmail.error}`;
        
        toast.success(message, { duration: 5000 });
      },
      onError: (error) => {
        toast.error(`Test fehlgeschlagen: ${error.response?.data?.error || error.message}`);
      }
    }
  );
  
  // Bulk test - asynchron mit Polling
  const [bulkTestRunning, setBulkTestRunning] = useState(false);
  
  const bulkTestServers = async () => {
    setBulkTestRunning(true);
    const toastId = toast.loading('🧪 Starte Bulk-Test...');
    
    try {
      // Starte Test
      const startResponse = await axios.post('/api/smtp/bulk-test/start', {
        testEmail: 'konrerushilun2p@gmx.net'
      });
      
      const testId = startResponse.data.data.test_id;
      const total = startResponse.data.data.total;
      
      toast.loading(`🧪 Teste ${total} SMTPs (mit IMAP-Check)...\n⏳ Dies dauert ~${Math.ceil(total * 15 / 60)} Minuten`, { id: toastId });
      
      // Polling bis fertig (max 5 Minuten wegen IMAP-Checks)
      let attempts = 0;
      const maxAttempts = 300; // 5 Minuten
      
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 3000)); // 3s Intervall
        
        const statusResponse = await axios.get(`/api/smtp/bulk-test/${testId}`);
        const data = statusResponse.data.data;
        
        // Progress-Update alle 5 Versuche (15s)
        if (attempts % 5 === 0 || attempts === 1) {
          const elapsed = Math.round(attempts * 3);
          const estimated = Math.ceil(total * 15);
          toast.loading(
            `🧪 Teste ${total} SMTPs...\n` +
            `⏳ ${elapsed}s / ~${estimated}s\n` +
            `📧 Mails gesendet, warte auf IMAP-Checks...`,
            { id: toastId }
          );
        }
        
        if (data.status === 'completed') {
          const inboxCount = data.results?.filter(r => r.delivery?.location === 'inbox').length || 0;
          const spamCount = data.results?.filter(r => r.delivery?.location === 'spam').length || 0;
          const notFoundCount = data.results?.filter(r => r.delivery?.location === 'not_found').length || 0;
          
          toast.success(
            `✅ Bulk-Test abgeschlossen:\n` +
            `📬 ${inboxCount} in Inbox\n` +
            `⚠️ ${spamCount} in Spam (deaktiviert)\n` +
            `❌ ${notFoundCount} nicht angekommen (deaktiviert)\n` +
            `Total: ${data.success}/${data.total} erfolgreich`,
            { id: toastId, duration: 8000 }
          );
          queryClient.invalidateQueries('smtpServers');
          break;
        }
        
        attempts++;
      }
      
      if (attempts >= maxAttempts) {
        toast.error('Bulk-Test Timeout (3 Min)', { id: toastId });
      }
      
    } catch (error) {
      toast.error('Bulk-Test fehlgeschlagen: ' + error.message, { id: toastId });
    } finally {
      setBulkTestRunning(false);
    }
  };
  
  // Test server function
  const testServer = (serverId) => {
    testServerMutation.mutate({ id: serverId, testType: 'all', testEmail: 'konrerushilun2p@gmx.net' });
  };

  // Update server mutation
  const updateServerMutation = useMutation(
    ({id, data}) => axios.put(`/api/smtp/${id}`, data),
    {
      onSuccess: () => {
        toast.success('SMTP-Server erfolgreich aktualisiert!');
        setEditingServer(null);
        queryClient.invalidateQueries('smtpServers');
      },
      onError: (error) => {
        toast.error('Fehler beim Aktualisieren des SMTP-Servers');
        console.error('SMTP update error:', error);
      }
    }
  );

  // Delete server mutation
  const deleteServerMutation = useMutation(
    (serverId) => axios.delete(`/api/smtp/${serverId}`),
    {
      onSuccess: () => {
        toast.success('SMTP-Server gelöscht!');
        queryClient.invalidateQueries('smtpServers');
      },
      onError: () => {
        toast.error('Fehler beim Löschen des SMTP-Servers');
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <Server className="h-6 w-6 mr-2 text-blue-600 dark:text-dark-copper-glow" />
              SMTP-Server
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie Ihre SMTP-Server für den E-Mail-Versand
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowCsvImport(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>📄 CSV Import</span>
            </button>
            
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={!servers || servers.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              <span>🗑️ Löschen</span>
            </button>
            
            <button
              onClick={() => setShowBulkParser(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
            >
              <Zap className="h-4 w-4" />
              <span>📦 Bulk Creator</span>
            </button>
            
            <button
              onClick={bulkTestServers}
              disabled={bulkTestRunning || !servers || servers.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TestTube className="h-4 w-4" />
              <span>{bulkTestRunning ? 'Teste...' : '🧪 Bulk Test'}</span>
            </button>
            
            {/* Admin Only: Proxy Reset Button */}
            {isAdmin && (
              <button
                onClick={handleProxyReset}
                disabled={proxyResetLoading}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                title="Alle 19 LTE-Proxies resetten (neue IPs)"
              >
                {proxyResetLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    <span>Resetting...</span>
                  </>
                ) : (
                  <span>🔄 Proxies Reset</span>
                )}
              </button>
            )}
            
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>{showAddForm ? 'Schließen' : 'SMTP hinzufügen'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk SMTP Creator Modal */}
      {showCsvImport && (
        <CsvSmtpImport
          onClose={() => setShowCsvImport(false)}
          onSuccess={() => {
            setShowCsvImport(false);
            queryClient.invalidateQueries('smtpServers');
          }}
        />
      )}
      
      {showBulkParser && (
        <BulkSmtpCreator
          onSuccess={() => {
            queryClient.invalidateQueries('smtpServers');
          }}
          onClose={() => setShowBulkParser(false)}
        />
      )}

      {/* Add SMTP Form */}
      {showAddForm && (
        <AddSmtpForm 
          onSubmit={(data) => addServerMutation.mutate(data)}
          loading={addServerMutation.isLoading}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Edit SMTP Form */}
      {editingServer && (
        <EditSmtpForm 
          server={editingServer}
          onSubmit={(data) => updateServerMutation.mutate({id: editingServer.id, data})}
          loading={updateServerMutation.isLoading}
          onCancel={() => setEditingServer(null)}
        />
      )}

      {/* SMTP Servers List */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg overflow-hidden border dark:border-dark-copper-dark">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-copper-dark">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Aktive SMTP-Server ({servers?.length || 0})
          </h2>
        </div>

        {servers && servers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-dark-surface">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Server
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Absender
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-dark-copper-dark">
                {servers.map((server) => (
                  <tr key={server.id} className={`hover:bg-gray-50 dark:hover:bg-dark-surface ${server.is_active === 0 ? 'opacity-50 bg-red-50 dark:bg-red-900/10' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Server className={`h-5 w-5 mr-3 ${server.is_active === 0 ? 'text-red-400' : 'text-gray-400'}`} />
                        <div>
                          <div className={`text-sm font-medium ${server.is_active === 0 ? 'text-red-600 dark:text-red-400 line-through' : 'text-gray-900 dark:text-gray-200'}`}>
                            {server.host}:{server.port}
                            {server.is_active === 0 && (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                                ❌ Inaktiv
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-300">
                            {server.encryption}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-200">{server.from_name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">{server.from_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge 
                        status={server.status_label} 
                        isActive={server.is_active}
                        deactivationReason={server.deactivation_reason}
                      />
                      {server.deactivation_reason && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {server.deactivation_reason.substring(0, 50)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                      {server.daily_limit || 'Unbegrenzt'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {new Date(server.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => testServer(server.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Server testen"
                        >
                          <TestTube className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingServer(server)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Bearbeiten"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteServerMutation.mutate(server.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Server className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-200">Keine SMTP-Server</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              Fügen Sie Ihren ersten SMTP-Server hinzu, um E-Mails zu versenden.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                SMTP-Server hinzufügen
              </button>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <BulkDeleteModal
          totalCount={servers?.length || 0}
          inactiveCount={(servers || []).filter((s) => s.is_active === 0).length}
          loading={bulkDeleteLoading}
          onClose={() => !bulkDeleteLoading && setShowDeleteModal(false)}
          onConfirm={performBulkDelete}
        />
      )}
    </div>
  );

};

const BulkDeleteModal = ({ totalCount, inactiveCount, loading, onClose, onConfirm }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-dark-copper-dark">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                SMTPs löschen
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Aktion kann nicht rückgängig gemacht werden
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <button
            type="button"
            disabled={loading || totalCount === 0}
            onClick={() => onConfirm('all')}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-red-200 dark:border-red-900/50 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-red-800 dark:text-red-300">
                Alle SMTPs löschen
              </div>
              <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                Entfernt jeden einzelnen Eintrag
              </div>
            </div>
            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 h-8 rounded-md bg-red-600 text-white text-sm font-bold">
              {totalCount}
            </span>
          </button>

          <button
            type="button"
            disabled={loading || inactiveCount === 0}
            onClick={() => onConfirm('inactive')}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-amber-200 dark:border-amber-900/50 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Nur inaktive SMTPs löschen
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                Entfernt ausschliesslich deaktivierte Eintraege
              </div>
            </div>
            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 h-8 rounded-md bg-amber-600 text-white text-sm font-bold">
              {inactiveCount}
            </span>
          </button>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-dark-copper-dark flex items-center justify-end gap-2 bg-gray-50 dark:bg-dark-surface/50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-dark-surface rounded-md disabled:opacity-50"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

// ALTE testServer Funktion war hier (entfernt - siehe Zeile 86)

// Status Badge Component mit Deliverability
const StatusBadge = ({ status, isActive, deactivationReason }) => {
  // Inaktiv
  if (isActive === 0) {
    if (deactivationReason?.includes('Spam') || status?.includes('Spam')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          <XCircle className="h-3 w-3 mr-1" />
          ⚠️ Spam
        </span>
      );
    }
    if (deactivationReason?.includes('Not found')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
          <AlertTriangle className="h-3 w-3 mr-1" />
          📭 Nicht gefunden
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
        <XCircle className="h-3 w-3 mr-1" />
        ❌ Inaktiv
      </span>
    );
  }
  
  // Aktiv - Deliverability
  if (status?.includes('Inbox')) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="h-3 w-3 mr-1" />
        📬 Inbox
      </span>
    );
  }
  
  // Default: OK
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
      <CheckCircle className="h-3 w-3 mr-1" />
      OK
    </span>
  );
};

// Edit SMTP Form Component
const EditSmtpForm = ({ server, onSubmit, loading, onCancel }) => {
  const [formData, setFormData] = useState({
    host: server.host || '',
    port: server.port || 587,
    username: server.username || '',
    password: server.password || '',
    encryption: server.encryption || 'tls',
    from_name: server.from_name || '',
    from_email: server.from_email || '',
    daily_limit: server.daily_limit || 1000
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        ✏️ SMTP-Server bearbeiten
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Server Settings */}
        <div className="bg-gray-50 dark:bg-dark-surface p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-4">🔧 Server-Einstellungen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                SMTP-Host *
              </label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => setFormData({...formData, host: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Port *
              </label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Verschlüsselung
              </label>
              <select
                value={formData.encryption}
                onChange={(e) => setFormData({...formData, encryption: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">Keine</option>
              </select>
            </div>
          </div>
        </div>

        {/* Auth Settings */}
        <div className="bg-yellow-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">🔐 Authentifizierung</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Benutzername *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Passwort *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Sender Settings */}
        <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">📤 Absender-Einstellungen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Absender-Name *
              </label>
              <input
                type="text"
                value={formData.from_name}
                onChange={(e) => setFormData({...formData, from_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Absender-E-Mail *
              </label>
              <input
                type="email"
                value={formData.from_email}
                onChange={(e) => setFormData({...formData, from_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Tägliches Limit
              </label>
              <input
                type="number"
                value={formData.daily_limit}
                onChange={(e) => setFormData({...formData, daily_limit: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? 'Aktualisieren...' : 'SMTP-Server aktualisieren'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Add SMTP Form Component
const AddSmtpForm = ({ onSubmit, loading, onCancel }) => {
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    encryption: 'tls',
    from_name: '',
    from_email: '',
    reply_to: '',
    daily_limit: 1000
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        ➕ SMTP-Server hinzufügen
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Server Settings */}
        <div className="bg-gray-50 dark:bg-dark-surface p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-4">🔧 Server-Einstellungen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                SMTP-Host *
              </label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => setFormData({...formData, host: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="smtp.gmail.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Port *
              </label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Verschlüsselung
              </label>
              <select
                value={formData.encryption}
                onChange={(e) => setFormData({...formData, encryption: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">Keine</option>
              </select>
            </div>
          </div>
        </div>

        {/* Auth Settings */}
        <div className="bg-yellow-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">🔐 Authentifizierung</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Benutzername *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ihr-username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Passwort *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ihr-passwort"
                required
              />
            </div>
          </div>
        </div>

        {/* Sender Settings */}
        <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">📤 Absender-Einstellungen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Absender-Name *
              </label>
              <input
                type="text"
                value={formData.from_name}
                onChange={(e) => setFormData({...formData, from_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ihr Name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Absender-E-Mail *
              </label>
              <input
                type="email"
                value={formData.from_email}
                onChange={(e) => setFormData({...formData, from_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="absender@domain.de"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Tägliches Limit
              </label>
              <input
                type="number"
                value={formData.daily_limit}
                onChange={(e) => setFormData({...formData, daily_limit: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                placeholder="1000"
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? 'Hinzufügen...' : 'SMTP-Server hinzufügen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmtpServers;
